/**
 * Fjärrpush (FCM på Android, APNs på iOS) som RESERV för lokala notiser: servern skickar bara när
 * telefonen inte har schemalagt larmet själv (t.ex. aktiviteten skapades på datorn medan appen var
 * stängd). Lokala notiser är fortfarande huvudvägen och fungerar offline.
 *
 * Säkerhetsregler:
 *  - Pluginet laddas och anropas BARA när servern säger att fjärrpush är påslaget. Utan Firebase-
 *    konfiguration (google-services.json) kan register() krascha appen på Android.
 *  - Behörighet begärs aldrig här – registrering sker bara om notisbehörigheten redan är beviljad
 *    (användaren ger den i Notisstatus).
 *  - Meddelandet innehåller ingen kalendertext. Ett tryck på en notis från ett annat konto öppnar
 *    ingenting i denna profil.
 */
export type RemoteProvider = 'fcm' | 'apns';

interface Handle {
  remove(): Promise<void>;
}

/** Den del av @capacitor/push-notifications som används (ersätts i tester). */
export interface RemotePushPlugin {
  checkPermissions(): Promise<{ receive: string }>;
  register(): Promise<void>;
  addListener(event: 'registration', cb: (t: { value: string }) => void): Promise<Handle>;
  addListener(event: 'registrationError', cb: (e: { error: string }) => void): Promise<Handle>;
  addListener(event: 'pushNotificationActionPerformed', cb: (a: { notification: { data?: unknown } }) => void): Promise<Handle>;
}

export type RemotePushResult = 'registered' | 'disabled' | 'no-permission' | 'error';

export interface RemotePushDeps {
  os: 'android' | 'ios';
  /** Profilens notismarkering (samma som för lokala notiser). */
  accountTag: string;
  /** /api/config → features.nativePush. Saknas på äldre servrar = avstängt. */
  serverFeatures: () => Promise<Partial<Record<RemoteProvider, boolean>> | undefined>;
  putToken: (provider: RemoteProvider, token: string) => Promise<void>;
  /** Tryck på en fjärrpush för denna profil: kvittera och synka. */
  onOpened: (deliveryId: string) => void;
  /** Tryck på en fjärrpush för en annan profil: visa bara startvyn. */
  onForeign: () => void;
  loadPlugin?: () => Promise<RemotePushPlugin>;
  registrationTimeoutMs?: number;
}

const defaultLoad = async (): Promise<RemotePushPlugin> => (await import('@capacitor/push-notifications')).PushNotifications as unknown as RemotePushPlugin;

export class RemotePushConnector {
  private tapListener = false;
  private done = false;
  private running: Promise<RemotePushResult> | null = null;

  constructor(private deps: RemotePushDeps) {}

  get provider(): RemoteProvider {
    return this.deps.os === 'ios' ? 'apns' : 'fcm';
  }

  /** Säker att anropa vid varje synk: gör ingenting efter lyckad registrering i denna appsession. */
  ensure(): Promise<RemotePushResult> {
    if (this.done) return Promise.resolve('registered');
    if (!this.running) this.running = this.run().finally(() => (this.running = null));
    return this.running;
  }

  private async run(): Promise<RemotePushResult> {
    const features = await this.deps.serverFeatures().catch(() => undefined);
    if (!features?.[this.provider]) return 'disabled';
    let plugin: RemotePushPlugin;
    try {
      plugin = await (this.deps.loadPlugin ?? defaultLoad)();
      if ((await plugin.checkPermissions()).receive !== 'granted') return 'no-permission';
      if (!this.tapListener) {
        this.tapListener = true;
        await plugin.addListener('pushNotificationActionPerformed', (a) => {
          const d = (a.notification.data ?? {}) as { accountTag?: unknown; deliveryId?: unknown };
          if (typeof d.accountTag === 'string' && d.accountTag === this.deps.accountTag && typeof d.deliveryId === 'string') this.deps.onOpened(d.deliveryId);
          else this.deps.onForeign();
        });
      }
      const token = await new Promise<string>((resolve, reject) => {
        const handles: Array<Promise<Handle>> = [];
        const timer = setTimeout(() => finish(() => reject(new Error('timeout'))), this.deps.registrationTimeoutMs ?? 15_000);
        const finish = (fn: () => void) => {
          clearTimeout(timer);
          for (const h of handles) void h.then((x) => x.remove()).catch(() => undefined);
          fn();
        };
        handles.push(plugin.addListener('registration', (t) => finish(() => resolve(t.value))));
        handles.push(plugin.addListener('registrationError', (e) => finish(() => reject(new Error(e.error)))));
        plugin.register().catch((e: unknown) => finish(() => reject(e instanceof Error ? e : new Error(String(e)))));
      });
      // Skickas vid varje appstart: tokenen kan ha roterats av FCM/APNs.
      await this.deps.putToken(this.provider, token);
      this.done = true;
      return 'registered';
    } catch {
      return 'error';
    }
  }
}
